// src/components/layout/Sidebar.jsx
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard, DollarSign, Globe, Users, Settings,
  HelpCircle, Shield, ShieldCheck, LogOut, CreditCard, Layers, ChevronDown, Wrench,
  Bug, PlusCircle, ChevronUp, ShoppingBag, ShoppingCart, Filter,
  PanelLeftClose, PanelLeftOpen, BarChart3, Video, Moon, Sun,
  FileText, Mail, FlaskConical, LineChart,
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

// Groups that are always expanded regardless of active route.
// Order here doesn't matter — render order is set by `navigation`.
const ALWAYS_EXPANDED = ['Ad Spend', 'E-commerce', 'Publisher'];

// ── Precise active check ──────────────────────────────────────────────────────
function useIsActive(href) {
  const location = useLocation();
  if (!href) return false;
  const [hrefPath, hrefQuery] = href.split('?');
  if (hrefQuery) {
    return location.pathname === hrefPath && location.search === `?${hrefQuery}`;
  }
  return location.pathname === hrefPath;
}

// ── Nav item ──────────────────────────────────────────────────────────────────
const NavItem = ({ item, onClose, isPro, collapsed }) => {
  const isActive = useIsActive(item.href);
  const isLocked = item.pro && !isPro;

  const cls = cn(
    'flex items-center rounded-lg transition-all hover:bg-accent',
    collapsed ? 'justify-center py-2.5 px-2' : 'gap-3 px-3 py-2 text-sm font-medium',
    isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
    isLocked && 'opacity-60',
  );

  return (
    <NavLink
      to={isLocked ? '/subscribe' : item.href}
      onClick={onClose}
      title={collapsed ? item.name : undefined}
      className={cls}
    >
      <item.icon className="h-4 w-4 flex-shrink-0" />
      {!collapsed && <span>{item.name}</span>}
      {!collapsed && isLocked && (
        <span className="ml-auto text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">PRO</span>
      )}
    </NavLink>
  );
};

// ── Nav group ─────────────────────────────────────────────────────────────────
const NavGroup = ({ item, onClose, isPro, collapsed }) => {
  const location = useLocation();
  const isChildActive = item.children?.some(c => {
    const [p, q] = (c.href || '').split('?');
    return q
      ? location.pathname === p && location.search === `?${q}`
      : location.pathname === p;
  });

  // ALWAYS_EXPANDED groups start open; others default to open if a child is active
  const [isOpen, setIsOpen] = useState(
    ALWAYS_EXPANDED.includes(item.name) ? true : (isChildActive ?? true)
  );

  if (collapsed) {
    return (
      <div className="space-y-0.5">
        {item.children?.map(child => (
          <NavItem key={child.href} item={child} onClose={onClose} isPro={isPro} collapsed />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent rounded-lg"
      >
        <div className="flex items-center gap-3">
          <item.icon className="h-4 w-4 flex-shrink-0" />
          <span>{item.name}</span>
        </div>
        <ChevronDown className={cn('h-4 w-4 flex-shrink-0 transition-transform', isOpen && 'rotate-180')} />
      </button>
      {isOpen && (
        <div className="ml-4 space-y-0.5 border-l border-border pl-3">
          {item.children?.map(child => (
            <NavItem key={child.href} item={child} onClose={onClose} isPro={isPro} collapsed={false} />
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================================================
export const Sidebar = ({ onClose }) => {
  const { profile, isAdmin, isDemo, isPro, tier, isTrialActive, daysRemaining, signOut } = useAuth();
  const { collapsed, setCollapsed } = useSidebar();
  const { isDark, toggleTheme } = useTheme();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handler = e => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getTierDisplay = () => {
    if (isDemo) return { label: 'Demo account', color: 'text-amber-500' };
    if (profile?.role === 'superadmin') return { label: 'Super Admin', color: 'text-purple-500' };
    if (profile?.role === 'admin') return { label: 'Admin', color: 'text-purple-500' };
    if (tier === 'pro') return { label: 'Pro Plan', color: 'text-primary' };
    if (tier === 'starter') return { label: 'Starter', color: 'text-blue-500' };
    if (isTrialActive) return { label: `Trial (${daysRemaining}d)`, color: 'text-yellow-600' };
    return { label: 'Trial Expired', color: 'text-red-500' };
  };
  const tierDisplay = getTierDisplay();

  const navigation = [
    { name: 'Overview',  href: '/dashboard', icon: LayoutDashboard },
    // Funnels, Landing Pages and A/B Tests are peers, not children of a
    // Project — Projects was removed from the product. Funnels and Landing
    // Pages are the same GrapesJS builder over the same table, split by
    // `kind` (multi-step vs single page); Results reports across both.
    { name: 'Funnels',       href: '/funnels',       icon: Filter },
    { name: 'Landing Pages', href: '/landing-pages', icon: FileText },
    { name: 'A/B Tests',     href: '/ab-tests',      icon: FlaskConical },
    { name: 'Results',       href: '/results',       icon: LineChart },
    {
      name: 'Ad Spend', icon: DollarSign,
      children: [
        { name: 'Meta Ads',    href: '/meta-ads',    icon: DollarSign },
        { name: 'Google Ads',  href: '/google-ads',  icon: DollarSign },
        { name: 'TikTok Ads',  href: '/tiktok-ads',  icon: Video },
      ],
    },
    // E-commerce sits above Publisher: it's the dual-source revenue group
    // (Shopify + TikTok Shop), mirroring Publisher's AdSense + Ad Manager
    // pair below. Sub-sections (products, orders, P&L, attribution, LTV,
    // exports) live inside the dashboard page itself — those used to be
    // separate sidebar entries but the nav is cleaner with three.
    {
      name: 'E-commerce', icon: ShoppingCart,
      children: [
        { name: 'Dashboard',      href: '/ecommerce?section=dashboard',   icon: LayoutDashboard },
        { name: 'TikTok Shop',    href: '/tiktok-shop',                   icon: ShoppingBag },
        { name: 'Shopify Stores', href: '/shopify',                       icon: ShoppingBag },
      ],
    },
    // Landing Pages used to live here as the legacy section builder. It's now
    // a top-level entry backed by the GrapesJS builder; the legacy list stays
    // reachable at /landing-pages/legacy for already-published pages.
    {
      name: 'Publisher', icon: BarChart3,
      children: [
        { name: 'Dashboard',  href: '/publisher',  icon: LayoutDashboard },
        { name: 'AdSense',    href: '/adsense',    icon: DollarSign },
        { name: 'Ad Manager', href: '/ad-manager', icon: Layers },
      ],
    },
    { name: 'HapiGuard',       href: '/hapiguard',       icon: ShieldCheck },
    { name: 'Custom Accounts', href: '/custom-accounts', icon: PlusCircle },
    { name: 'Custom Domains',  href: '/custom-domains',  icon: Globe, pro: true },
  ];

  const userMenuItems = [
    { name: 'Team',      href: '/team',      icon: Users },
    { name: 'Affiliate', href: '/affiliate', icon: Users },
    { name: 'Subscribe', href: '/subscribe', icon: CreditCard },
    { name: 'Support',   href: '/support',   icon: HelpCircle },
    { name: 'Settings',  href: '/settings',  icon: Settings },
  ];

  const handleSignOut = async () => {
    try { await signOut(); window.location.href = '/login'; } catch {}
  };

  return (
    <div className="flex h-full flex-col bg-card border-r border-border overflow-hidden">

      {/* Logo + collapse toggle */}
      <div className="flex h-16 items-center border-b border-border px-3 gap-2 flex-shrink-0">
        {!collapsed && (
          <>
            <img src="/logo-icon.svg" alt="RevHapi" className="h-8 w-8 rounded-lg flex-shrink-0" />
            <span className="text-lg font-semibold flex-1 truncate">RevHapi</span>
          </>
        )}
        <button
          onClick={() => setCollapsed(v => !v)}
          className={`p-1.5 rounded-lg hover:bg-accent text-muted-foreground flex-shrink-0 ${collapsed ? 'mx-auto' : 'ml-auto'}`}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {navigation.map(item =>
          item.children
            ? <NavGroup key={item.name} item={item} onClose={onClose} isPro={isPro} collapsed={collapsed} />
            : <NavItem key={item.href} item={item} onClose={onClose} isPro={isPro} collapsed={collapsed} />
        )}

        {/* Admin section */}
        {(isAdmin || profile?.role === 'superadmin' || profile?.role === 'admin') && (
          <>
            <div className="my-3 border-t border-border" />
            {!collapsed && (
              <p className="px-3 mb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Admin</p>
            )}
            {[
              { name: 'Admin Panel',     href: '/admin',           icon: Shield },
              { name: 'System Admin',    href: '/system-admin',    icon: Wrench },
              { name: 'Email Marketing', href: '/email-marketing', icon: Mail },
              { name: 'Debug',           href: '/debug',           icon: Bug },
            ].map(item => (
              <NavItem key={item.href} item={item} onClose={onClose} isPro collapsed={collapsed} />
            ))}
          </>
        )}
      </nav>

      {/* User section */}
      <div className="border-t border-border p-2 relative" ref={userMenuRef}>
        {showUserMenu && (
          <div className={cn(
            'absolute z-50 bg-card border border-border rounded-lg shadow-lg overflow-hidden',
            collapsed ? 'bottom-2 left-full ml-2 w-52' : 'bottom-full left-0 right-0 mb-1 mx-1'
          )}>
            {userMenuItems.map(item => (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={() => { setShowUserMenu(false); onClose?.(); }}
                className={({ isActive }) => cn(
                  'flex items-center gap-3 px-4 py-2.5 text-sm font-medium hover:bg-accent transition-all',
                  isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
                )}
              >
                <item.icon className="h-4 w-4" />{item.name}
              </NavLink>
            ))}
            <div className="border-t border-border" />
            <button
              onClick={() => { setShowUserMenu(false); handleSignOut(); }}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20"
            >
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>
        )}

        {/* Dark mode toggle */}
        {collapsed ? (
          <button onClick={toggleTheme}
            className="flex w-full items-center justify-center p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground mb-1"
            title={isDark ? 'Light mode' : 'Dark mode'}>
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        ) : (
          <button onClick={toggleTheme}
            className="flex w-full items-center gap-3 px-3 py-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground text-sm mb-1">
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
          </button>
        )}

        <button
          onClick={() => setShowUserMenu(!showUserMenu)}
          className="flex w-full items-center gap-2 p-2 rounded-lg hover:bg-accent transition-colors"
        >
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
            {profile?.username?.[0]?.toUpperCase() || profile?.email?.[0]?.toUpperCase() || '?'}
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium truncate">
                  {profile?.username || profile?.email?.split('@')[0] || 'User'}
                </p>
                <p className={cn('text-xs truncate', tierDisplay.color)}>{tierDisplay.label}</p>
              </div>
              <ChevronUp className={cn('h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform', !showUserMenu && 'rotate-180')} />
            </>
          )}
        </button>
      </div>
    </div>
  );
};
